import React, { useState, useEffect, useCallback } from 'react';
import customerService from '../services/customerService';
import CustomerForm from '../components/customers/CustomerForm';
import CustomerViewModal from '../components/customers/CustomerViewModal';
import { FiPlusCircle, FiEdit, FiTrash2, FiSearch, FiEye } from 'react-icons/fi';
import CashierQuickActions from '../components/common/CashierQuickActions';

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');

  // --- THE FIX IS HERE ---
  // This function now only fetches data if a search term exists.
  const fetchData = useCallback((search) => {
    if (!search) {
      setCustomers([]); // Clear results if search is empty
      return;
    }
    customerService.getCustomers(search)
      .then(setCustomers)
      .catch(() => setError('Failed to fetch customers.'));
  }, []);

  // This useEffect now triggers the search only when the searchTerm changes.
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchData(searchTerm);
    }, 300); // Debounce to avoid API calls on every keystroke

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, fetchData]);
  // --- END OF FIX ---

  const handleOpenEditModal = (customer = null) => {
    setSelectedCustomer(customer);
    setModalError('');
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setSelectedCustomer(null);
    setModalError('');
    setIsEditModalOpen(false);
  };

  const handleOpenViewModal = (customer) => {
    setSelectedCustomer(customer);
    setIsViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setSelectedCustomer(null);
    setIsViewModalOpen(false);
  };

  const handleSaveCustomer = async (formData, createLoyaltyCard) => {
    const dataToSend = { ...formData, createLoyaltyCard };
    try {
      if (selectedCustomer) {
        await customerService.updateCustomer(selectedCustomer._id, dataToSend);
      } else {
        await customerService.createCustomer(dataToSend);
      }
      setModalError('');
      fetchData(searchTerm);
      handleCloseEditModal();
    } catch (err) {
      setModalError(err.response?.data?.message || 'Failed to save customer.');
    }
  };

  const handleDeleteCustomer = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await customerService.deleteCustomer(id);
        fetchData(searchTerm);
      } catch (err) {
        setError('Failed to delete customer.');
      }
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <CashierQuickActions />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Customer Management</h1>
        <button onClick={() => handleOpenEditModal()} className="flex items-center bg-primary text-white px-4 py-2 rounded-md shadow hover:bg-primary-dark">
          <FiPlusCircle className="mr-2" /> Add New Customer
        </button>
      </div>
      
      <div className="mb-4 relative">
        <input type="text" placeholder="Search by Name, Email, Phone, or Customer ID..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full p-2 pl-10 border rounded-md" />
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      </div>

      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      <div className="bg-white shadow-md rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">Customer ID</th>
              <th className="px-6 py-3 text-left">Name</th>
              <th className="px-6 py-3 text-left">Email</th>
              <th className="px-6 py-3 text-left">Phone</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* --- UI IMPROVEMENT --- */}
            {customers.length === 0 && searchTerm === '' && (
              <tr>
                <td colSpan="5" className="text-center py-10 text-gray-500">
                  Please use the search bar to find a customer.
                </td>
              </tr>
            )}
            {customers.length === 0 && searchTerm !== '' && (
              <tr>
                <td colSpan="5" className="text-center py-10 text-gray-500">
                  No customers found for "{searchTerm}".
                </td>
              </tr>
            )}
            {/* --- END OF UI IMPROVEMENT --- */}
            {customers.map(customer => (
              <tr key={customer._id}>
                <td className="px-6 py-4 font-mono text-sm">{customer.customerId}</td>
                <td className="px-6 py-4 font-medium">{customer.fullName}</td>
                <td className="px-6 py-4">{customer.email}</td>
                <td className="px-6 py-4">{customer.phone}</td>
                <td className="px-6 py-4 text-right space-x-4">
                    <button onClick={() => handleOpenViewModal(customer)} className="text-gray-500 hover:text-blue-600" title="View Details">
                        <FiEye size={18} />
                    </button>
                    <button onClick={() => handleOpenEditModal(customer)} className="text-indigo-600 hover:text-indigo-900" title="Quick Edit">
                        <FiEdit size={18} />
                    </button>
                    <button onClick={() => handleDeleteCustomer(customer._id)} className="text-red-600 hover:text-red-900" title="Delete Customer">
                        <FiTrash2 size={18} />
                    </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isEditModalOpen && 
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
            <h2 className="text-2xl font-bold mb-4">{selectedCustomer ? 'Edit Customer' : 'Add New Customer'}</h2>
            {modalError && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{modalError}</p>}
            <CustomerForm customer={selectedCustomer} onSave={handleSaveCustomer} onCancel={handleCloseEditModal} />
          </div>
        </div>
      }

      {isViewModalOpen &&
        <CustomerViewModal customer={selectedCustomer} onCancel={handleCloseViewModal} />
      }
    </div>
  );
};

export default CustomersPage;
